import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ProblemDetail from './ProblemDetail';
import QuantTracker from './QuantTracker';
import { Helmet } from 'react-helmet-async';
import { formatTime, getTodayTime, saveTodayTime, getTotalTime, getTotalTodayTime, getTotalYesterdayTime, getTotalThisWeekTime } from '../utils/timeUtils';
import QuantFeedbackTracker from './QuantFeedbackTracker';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import ReactMarkdown from 'react-markdown';
import { dataService } from '../services/dataService';
import { storageService } from '../services/storageService';
import { supabase } from '../services/supabaseClient';

interface ProblemRecord {
  id: string;
  problemNumber: number;
  timeSpent: number; // in seconds
  passed: boolean;
  timestamp: number;
  aiSolution?: string;
  date: string;
  // Optional feedback fields (same as error records)
  errorType?: string;
  errorMade?: string;
  difficulty?: string;
  notes?: string;
  image?: string;
}

interface QuantErrorRecord {
  id: string;
  problemNumber: number;
  errorType: string;
  errorMade: string;
  difficulty: string;
  notes?: string;
  timestamp: number;
  date: string;
  image?: string;
  timeSpent?: number;
  isFromProblemTracker?: boolean;
}

interface DebugLog {
  timestamp: number;
  type: 'fail' | 'fail_complete' | 'error_tracker' | 'error_complete' | 'error_cancel' | 'pass' | 'feedback' | 'state' | 'error' | 'timer' | 'localStorage' | 'mount' | 'unmount' | 'form' | 'review' | 'storage_full' | 'storage';
  message: string;
  data?: any;
}

type DebugLogger = (type: DebugLog['type'], message: string, data?: any) => void;

// Image compression utility
const compressImage = (base64String: string, maxWidth: number = 800, quality: number = 0.7): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to JPEG with quality setting
      const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedBase64);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = base64String;
  });
};

// Cleanup old entries when quota is exceeded
const cleanupOldEntries = (items: any[], maxItems: number = 50): any[] => {
  // Sort by timestamp (oldest first) and keep only the most recent entries
  const sorted = [...items].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  return sorted.slice(-maxItems);
};

// Helper function to calculate localStorage size
const getLocalStorageSize = (): { total: number; breakdown: { [key: string]: number } } => {
  let total = 0;
  const breakdown: { [key: string]: number } = {};
  
  for (let key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      const value = localStorage.getItem(key) || '';
      const size = new Blob([value]).size;
      breakdown[key] = size;
      total += size;
    }
  }
  
  return { total, breakdown };
};

// Helper function to estimate quota (if available)
const getStorageQuotaInfo = async (): Promise<any> => {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const estimate = await navigator.storage.estimate();
      const result: any = {
        quota: estimate.quota,
        usage: estimate.usage
      };
      // usageDetails might not be available in all browsers
      if ('usageDetails' in estimate) {
        result.usageDetails = (estimate as any).usageDetails;
      }
      return result;
    } catch (e) {
      return { error: String(e) };
    }
  }
  return { available: false };
};

const mapSupabaseProblemRecord = (record: any): ProblemRecord => {
  const timestamp =
    typeof record?.timestamp === 'number'
      ? record.timestamp
      : Number(record?.timestamp ?? Date.now());
  return {
    id: record?.id
      ? String(record.id)
      : record?.timestamp?.toString() || `temp-${Date.now()}`,
    problemNumber: record?.problem_number ?? 0,
    timeSpent: typeof record?.time_spent === 'number' ? record.time_spent : 0,
    passed: Boolean(record?.passed),
    timestamp,
    date: record?.date || new Date(timestamp).toISOString().slice(0, 10),
    errorType: record?.error_type || undefined,
    errorMade: record?.error_made || undefined,
    difficulty: record?.difficulty || undefined,
    notes: record?.notes || undefined,
    image: record?.image || undefined,
    aiSolution: record?.ai_solution || undefined
  };
};

const mapSupabaseErrorRecord = (record: any): QuantErrorRecord => {
  const timestamp =
    typeof record?.timestamp === 'number'
      ? record.timestamp
      : Number(record?.timestamp ?? Date.now());
  return {
    id: record?.id
      ? String(record.id)
      : record?.timestamp?.toString() || `temp-${Date.now()}`,
    problemNumber: record?.problem_number ?? 0,
    errorType: record?.error_type || 'Not specified',
    errorMade: record?.error_made || 'Not specified',
    difficulty: record?.difficulty || 'Unknown',
    notes: record?.notes || undefined,
    timestamp,
    date: record?.date || new Date(timestamp).toISOString().slice(0, 10),
    image: record?.image || undefined,
    timeSpent: typeof record?.time_spent === 'number' ? record.time_spent : undefined,
    isFromProblemTracker: true
  };
};

const isBase64Image = (value?: string | null): value is string =>
  !!value && value.startsWith('data:');

// Toast notification component
const Toast: React.FC<{ message: string; show: boolean; onClose: () => void }> = ({ message, show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000); // Auto-close after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'var(--color-success)',
        color: 'white',
        padding: '15px 25px',
        borderRadius: '16px',
        boxShadow: '0 4px 6px rgba(31, 76, 64, 0.25)',
        zIndex: 10000,
        fontSize: '16px',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        animation: show ? 'slideDown 0.3s ease-out' : 'none'
      }}
    >
      <span>{message}</span>
      <button
        onClick={onClose}
        style={{
          background: 'none',
          border: 'none',
          color: 'white',
          fontSize: '20px',
          cursor: 'pointer',
          padding: '0',
          marginLeft: '10px',
          lineHeight: '1'
        }}
      >
        ×
      </button>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

const LEGACY_QUANT_MIGRATION_FLAG = 'gre_quant_legacy_synced_v1';

const PRIMARY = 'var(--color-primary)';
const PRIMARY_DARK = 'var(--color-primary-dark)';
const BORDER = 'var(--color-border)';
const SURFACE = 'var(--color-surface)';
const TEXT_MUTED = 'var(--color-text-muted)';

const quantCardStyle: React.CSSProperties = {
  backgroundColor: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: 'var(--radius-card)',
  boxShadow: 'var(--shadow-card)',
  padding: '24px'
};

const statTileStyle: React.CSSProperties = {
  flex: '1 1 160px',
  minWidth: '150px',
  backgroundColor: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: '20px',
  padding: '14px 16px',
  boxShadow: '0 6px 18px rgba(31,76,64,0.06)',
  textAlign: 'center'
};

const barBackgroundStyle: React.CSSProperties = {
  width: '100%',
  height: '12px',
  borderRadius: '999px',
  backgroundColor: 'var(--color-bar-bg)',
  overflow: 'hidden'
};

const QuantProblemTracker: React.FC = () => {
  const [currentProblem, setCurrentProblem] = useState(1);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [problemTime, setProblemTime] = useState(0); // Time for current problem in seconds
  const [showFeedback, setShowFeedback] = useState(false);
  const [showErrorForm, setShowErrorForm] = useState(false);
  const [showFeedbackTracker, setShowFeedbackTracker] = useState(false);
  const { user } = useAuth();
  const { isPro, isTrial } = useSubscription();
  const navigate = useNavigate();
  const [showAILimitBanner, setShowAILimitBanner] = useState(false);
  const [solvingId, setSolvingId] = useState<string | null>(null);
  const [liveImage, setLiveImage] = useState<string | null>(null);
  const [liveAISolution, setLiveAISolution] = useState<string | null>(null);
  const [liveAISolving, setLiveAISolving] = useState(false);
  const [showLiveAILimitBanner, setShowLiveAILimitBanner] = useState(false);
  const [problems, setProblems] = useState<ProblemRecord[]>([]);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [selectedProblem, setSelectedProblem] = useState<ProblemRecord | null>(null);
  const [errors, setErrors] = useState<QuantErrorRecord[]>([]);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [isClearingHistory, setIsClearingHistory] = useState(false);
  const [showProblemList, setShowProblemList] = useState(false);
  const [quantSessionActive, setQuantSessionActive] = useState(false);
  const [showPerformance, setShowPerformance] = useState(false);

  
  
  // Error form state
  const [errorType, setErrorType] = useState('');
  const [errorMade, setErrorMade] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [notes, setNotes] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  
  // Error form options
  const errorTypes = [
    'Algebra',
    'Geometry',
    'Arithmetic',
    'Data Interpretation',
    'Word Problems',
    'Number Properties',
    'Probability',
    'Statistics',
    'Sequences',
    'Functions',
    'Inequalities',
    'Exponents & Roots',
    'Combinations',
    'Other'
  ];
  
  const errorMades = [
    'Careless mistake',
    'Forgot formula',
    'Forgot shortcut',
    'Misread question',
    'Calculation error',
    'Conceptual misunderstanding',
    'Time management',
    'Didn\'t check answer',
    'Misapplied formula',
    'Other'
  ];
  
  const difficulties = ['Easy', 'Medium', 'Hard'];
  
  // Quant mode timer (total time spent in quant mode)
  const [quantTime, setQuantTime] = useState(0);
  const [quantTodayTime, setQuantTodayTime] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [totalTodayTime, setTotalTodayTime] = useState(0);
  const [vocabTime, setVocabTime] = useState(0);
  const [vocabTodayTime, setVocabTodayTime] = useState(0);
  
  const sessionStartTimeRef = useRef<number | null>(null);
  const todaySessionStartRef = useRef<number | null>(null);
  const problemStartTimeRef = useRef<number | null>(null);
  const initialSavedTimeRef = useRef<number>(0);
  const initialTodayTimeRef = useRef<number>(0);
  const lastSyncedProblemRef = useRef<number | null>(null);

  // Debug logging function
  const addDebugLog: DebugLogger = useCallback((_type, _message, _data) => {
    // suppressed in production
  }, []);

  const uploadImageIfNeeded = useCallback(async (
    image?: string | null,
    context: 'pass' | 'error' | 'feedback' = 'pass'
  ): Promise<string | undefined> => {
    if (!user || !isBase64Image(image)) {
      return image || undefined;
    }
    try {
      const result = await storageService.uploadProblemImage(image, user.id, context);
      if (result?.error) {
        addDebugLog('error', 'Supabase image upload failed', { context, error: result.error });
        return image || undefined;
      }
      addDebugLog('storage', 'Image uploaded to Supabase', {
        context,
        path: result?.path
      });
      return result?.publicUrl || result?.path || image || undefined;
    } catch (error) {
      addDebugLog('error', 'Image upload threw exception', {
        context,
        error: error instanceof Error ? error.message : String(error)
      });
      return image || undefined;
    }
  }, [user, addDebugLog]);

  const getImageSrc = (image?: string | null): string | undefined =>
    storageService.getPublicUrl(image);

  useEffect(() => {
    if (!user || localStorage.getItem(LEGACY_QUANT_MIGRATION_FLAG) === 'true') {
      return;
    }

    let cancelled = false;

    const migrateLegacyQuantData = async () => {
      try {
        const savedProblems = localStorage.getItem('gre_quant_problems');
        const legacyProblems: ProblemRecord[] = savedProblems ? JSON.parse(savedProblems) : [];
        let problemsMigrated = 0;

        for (const problem of legacyProblems) {
          if (cancelled) return;
          try {
            const preparedImage = await uploadImageIfNeeded(problem.image, problem.passed ? 'pass' : 'error');
            await dataService.quantProblems.insert({
              user_id: user.id,
              problem_number: problem.problemNumber ?? 0,
              time_spent: typeof problem.timeSpent === 'number' ? problem.timeSpent : 0,
              passed: Boolean(problem.passed),
              timestamp: problem.timestamp ?? Date.now(),
              date: problem.date || new Date().toISOString().slice(0, 10),
              error_type: problem.errorType || undefined,
              error_made: problem.errorMade || undefined,
              difficulty: problem.difficulty || undefined,
              notes: problem.notes || undefined,
              image: preparedImage || undefined
            });
            problemsMigrated += 1;
          } catch (err: any) {
            addDebugLog('error', 'Failed to migrate legacy problem', {
              problemNumber: problem.problemNumber,
              errorMessage: err?.message || String(err)
            });
          }
        }

        const savedErrors = localStorage.getItem('gre_quant_errors');
        const legacyErrors: QuantErrorRecord[] = savedErrors ? JSON.parse(savedErrors) : [];
        let errorsMigrated = 0;

        for (const errorRecord of legacyErrors) {
          if (cancelled) return;
          try {
            const preparedImage = await uploadImageIfNeeded(errorRecord.image, 'error');
            await dataService.quantErrors.insert({
              user_id: user.id,
              problem_number: errorRecord.problemNumber ?? 0,
              error_type: errorRecord.errorType || 'Not specified',
              error_made: errorRecord.errorMade || 'Not specified',
              difficulty: errorRecord.difficulty || 'Unknown',
              notes: errorRecord.notes || undefined,
              image: preparedImage || undefined,
              time_spent: typeof errorRecord.timeSpent === 'number' ? errorRecord.timeSpent : undefined,
              timestamp: errorRecord.timestamp ?? Date.now(),
              date: errorRecord.date || new Date().toISOString().slice(0, 10)
            });
            errorsMigrated += 1;
          } catch (err: any) {
            addDebugLog('error', 'Failed to migrate legacy error', {
              problemNumber: errorRecord.problemNumber,
              errorMessage: err?.message || String(err)
            });
          }
        }

        const savedFeedback = localStorage.getItem('gre_quant_feedback');
        const legacyFeedbacks: any[] = savedFeedback ? JSON.parse(savedFeedback) : [];
        let feedbackMigrated = 0;

        for (const feedback of legacyFeedbacks) {
          if (cancelled) return;
          try {
            const preparedImage = await uploadImageIfNeeded(feedback.image, 'feedback');
            await dataService.quantFeedback.insert({
              user_id: user.id,
              problem_number: feedback.problemNumber ?? 0,
              error_type: feedback.errorType || undefined,
              error_made: feedback.errorMade || undefined,
              difficulty: feedback.difficulty || undefined,
              notes: feedback.notes || undefined,
              image: preparedImage || undefined,
              timestamp: feedback.timestamp ?? Date.now(),
              date: feedback.date || new Date().toISOString().slice(0, 10)
            });
            feedbackMigrated += 1;
          } catch (err: any) {
            addDebugLog('error', 'Failed to migrate legacy feedback', {
              problemNumber: feedback.problemNumber,
              errorMessage: err?.message || String(err)
            });
          }
        }

        localStorage.setItem(LEGACY_QUANT_MIGRATION_FLAG, 'true');
        addDebugLog('state', 'Legacy quant data migrated to Supabase', {
          problemsMigrated,
          errorsMigrated,
          feedbackMigrated
        });
      } catch (error: any) {
        addDebugLog('error', 'Legacy quant data migration failed', {
          errorMessage: error?.message || String(error)
        });
      }
    };

    migrateLegacyQuantData();

    return () => {
      cancelled = true;
    };
  }, [user, uploadImageIfNeeded, addDebugLog]);


  const syncProblemToSupabase = async (problem: ProblemRecord) => {
    if (!user) return;
    try {
      const { data, error } = await dataService.quantProblems.insert({
        user_id: user.id,
        problem_number: problem.problemNumber,
        time_spent: problem.timeSpent,
        passed: problem.passed,
        timestamp: problem.timestamp,
        date: problem.date,
        error_type: problem.errorType || undefined,
        error_made: problem.errorMade || undefined,
        difficulty: problem.difficulty || undefined,
        notes: problem.notes || undefined,
        image: problem.image || undefined
      });
      if (error) {
        addDebugLog('error', `Supabase problem sync error: ${error.message}`, error);
      } else if (data) {
        setProblems(prev =>
          prev.map(p => (p.id === problem.id ? { ...p, id: data.id } : p))
        );
      }
    } catch (e: any) {
      addDebugLog('error', 'Supabase problem sync unexpected error', {
        errorMessage: e?.message || String(e)
      });
    }
  };

  const syncErrorToSupabase = async (errorRecord: QuantErrorRecord) => {
    if (!user) return;
    try {
      const { data, error } = await dataService.quantErrors.insert({
        user_id: user.id,
        problem_number: errorRecord.problemNumber,
        error_type: errorRecord.errorType,
        error_made: errorRecord.errorMade,
        difficulty: errorRecord.difficulty,
        notes: errorRecord.notes || undefined,
        image: errorRecord.image || undefined,
        time_spent: typeof errorRecord.timeSpent === 'number' ? errorRecord.timeSpent : undefined,
        timestamp: errorRecord.timestamp,
        date: errorRecord.date
      });
      if (error) {
        addDebugLog('error', `Supabase error sync error: ${error.message}`, error);
      } else if (data) {
        setErrors(prev =>
          prev.map(err => (err.id === errorRecord.id ? { ...err, id: data.id } : err))
        );
      }
    } catch (e: any) {
      addDebugLog('error', 'Supabase error sync unexpected error', {
        errorMessage: e?.message || String(e)
      });
    }
  };

  // Log component mount
  useEffect(() => {
    addDebugLog('mount', 'Component mounted', {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      localStorageAvailable: typeof Storage !== 'undefined',
      timestamp: Date.now()
    });

    return () => {
      addDebugLog('unmount', 'Component unmounting');
    };
  }, [addDebugLog]);

  // Load saved data on mount
  useEffect(() => {
    try {
      if (!user) {
        addDebugLog('localStorage', 'Starting to load data from localStorage');
        
        // Test localStorage availability
        try {
          const testKey = '__test__';
          localStorage.setItem(testKey, 'test');
          localStorage.removeItem(testKey);
          addDebugLog('localStorage', 'localStorage is available and working');
        } catch (e) {
          addDebugLog('error', `localStorage test failed: ${e}`, e);
        }
        
        // Load problems
        const savedProblems = localStorage.getItem('gre_quant_problems');
        addDebugLog('localStorage', `Raw problems data: ${savedProblems ? savedProblems.substring(0, 200) + '...' : 'null'}`);
        
        if (savedProblems) {
          try {
            const parsed = JSON.parse(savedProblems);
            addDebugLog('localStorage', `Successfully parsed ${parsed.length} problems`, parsed);
            setProblems(parsed);
            // Set current problem to next number
            if (parsed.length > 0) {
              const maxProblem = Math.max(...parsed.map((p: ProblemRecord) => p.problemNumber));
              setCurrentProblem(maxProblem + 1);
              addDebugLog('state', `Set current problem to ${maxProblem + 1}`);
            }
          } catch (e) {
            addDebugLog('error', `Error parsing problems: ${e}`, e);
          }
        } else {
          addDebugLog('localStorage', 'No saved problems found in localStorage');
        }
        
        // Load errors from localStorage
        const savedErrors = localStorage.getItem('gre_quant_errors');
        addDebugLog('localStorage', `Raw errors data: ${savedErrors ? savedErrors.substring(0, 200) + '...' : 'null'}`);
        
        if (savedErrors) {
          try {
            const parsedErrors = JSON.parse(savedErrors);
            setErrors(parsedErrors);
            addDebugLog('localStorage', `Successfully parsed ${parsedErrors.length} errors`, parsedErrors);
          } catch (e) {
            addDebugLog('error', `Error parsing errors: ${e}`, e);
          }
        } else {
          addDebugLog('localStorage', 'No errors found in localStorage');
        }
      } else {
        addDebugLog('state', 'Skipping local problem/error load (using Supabase data)', { user: user.id });
      }
      
      // Load quant time (only once on mount)
      const savedTime = localStorage.getItem('gre_quant_time');
      if (savedTime) {
        try {
          const savedTimeSeconds = parseInt(savedTime, 10);
          if (!isNaN(savedTimeSeconds) && savedTimeSeconds >= 0) {
            initialSavedTimeRef.current = savedTimeSeconds;
            setQuantTime(savedTimeSeconds);
          }
        } catch (e) {
          console.error('Error parsing saved quant time:', e);
        }
      }
      
      // Load today's quant time (only once on mount)
      const todayTimeValue = getTodayTime('quant');
      initialTodayTimeRef.current = todayTimeValue;
      setQuantTodayTime(todayTimeValue);
      
      // Load vocab times
      const flashcardTime = parseInt(localStorage.getItem('gre_flashcard_time') || '0', 10);
      const synonymTime = parseInt(localStorage.getItem('gre_synonym_time') || '0', 10);
      const fastAttackTime = parseInt(localStorage.getItem('gre_fast_attack_time') || '0', 10);
      const hardTermsTime = parseInt(localStorage.getItem('gre_hard_terms_time') || '0', 10);
      setVocabTime(flashcardTime + synonymTime + fastAttackTime + hardTermsTime);
      
      const flashcardToday = getTodayTime('flashcard');
      const synonymToday = getTodayTime('synonym');
      const fastAttackToday = getTodayTime('fastattack');
      const hardTermsToday = getTodayTime('hardterms');
      setVocabTodayTime(flashcardToday + synonymToday + fastAttackToday + hardTermsToday);
      
      // Load total times
      setTotalTime(getTotalTime());
      setTotalTodayTime(getTotalTodayTime());
      
      // Start tracking from now
      sessionStartTimeRef.current = Date.now();
      todaySessionStartRef.current = Date.now();
    } catch (e) {
      console.error('Error loading data:', e);
    }
  }, [user, addDebugLog]);

  useEffect(() => {
    if (!user) return;
    let isCancelled = false;

    const loadSupabaseData = async () => {
      addDebugLog('state', 'Loading quant data from Supabase');
      try {
        const [
          problemsRes,
          errorsRes,
          progressRes
        ] = await Promise.all([
          dataService.quantProblems.getAll(user.id),
          dataService.quantErrors.getAll(user.id),
          dataService.userProgressState.get(user.id, 'quant', 'current_problem')
        ]);

        if (isCancelled) return;

        if (problemsRes.error) {
          addDebugLog('error', `Error loading problems from Supabase: ${problemsRes.error.message}`, problemsRes.error);
        } else if (problemsRes.data) {
          const supabaseProblems = problemsRes.data.map(mapSupabaseProblemRecord);
          setProblems(supabaseProblems);
          try {
            localStorage.setItem('gre_quant_problems', JSON.stringify(supabaseProblems));
          } catch (e) {
            addDebugLog('error', 'Error caching Supabase problems locally', e);
          }
          const maxProblemNumber = supabaseProblems.length > 0
            ? Math.max(...supabaseProblems.map(p => p.problemNumber))
            : 0;
          if (maxProblemNumber > 0) {
            setCurrentProblem(prev => Math.max(prev, maxProblemNumber + 1));
          }
        }

        if (errorsRes.error) {
          addDebugLog('error', `Error loading errors from Supabase: ${errorsRes.error.message}`, errorsRes.error);
        } else if (errorsRes.data) {
          const supabaseErrors = errorsRes.data.map(mapSupabaseErrorRecord);
          setErrors(supabaseErrors);
          try {
            localStorage.setItem('gre_quant_errors', JSON.stringify(supabaseErrors));
          } catch (e) {
            addDebugLog('error', 'Error caching Supabase errors locally', e);
          }
        }

        const progressState = progressRes?.data?.state_value
          ? parseInt(progressRes.data.state_value, 10)
          : null;
        if (!Number.isNaN(progressState || NaN) && progressState) {
          setCurrentProblem(prev => Math.max(prev, progressState));
        }
      } catch (e: any) {
        addDebugLog('error', 'Unexpected error loading Supabase data', {
          errorMessage: e?.message || String(e)
        });
      }
    };

    loadSupabaseData();

    return () => {
      isCancelled = true;
    };
  }, [user, addDebugLog]);

  useEffect(() => {
    if (!user || !currentProblem) return;
    if (lastSyncedProblemRef.current === currentProblem) return;
    lastSyncedProblemRef.current = currentProblem;

    dataService.userProgressState
      .upsert({
        user_id: user.id,
        game_mode: 'quant',
        state_key: 'current_problem',
        state_value: currentProblem.toString()
      })
      .catch(err => {
        addDebugLog('error', 'Error syncing current problem to Supabase', err);
      });
  }, [currentProblem, user, addDebugLog]);

  // Timer effect for quant mode time — only starts after user action
  useEffect(() => {
    if (!quantSessionActive) return;

    if (!sessionStartTimeRef.current) {
      sessionStartTimeRef.current = Date.now();
    }
    if (!todaySessionStartRef.current) {
      todaySessionStartRef.current = Date.now();
    }

    const interval = setInterval(() => {
      if (sessionStartTimeRef.current && todaySessionStartRef.current) {
        const now = Date.now();
        
        // Quant mode timer - use initial saved time + current session elapsed
        const sessionElapsed = Math.floor((now - sessionStartTimeRef.current) / 1000);
        const totalElapsed = initialSavedTimeRef.current + sessionElapsed;
        setQuantTime(totalElapsed);
        
        // Today's quant timer
        const todaySessionElapsed = Math.floor((now - todaySessionStartRef.current) / 1000);
        const totalTodayElapsed = initialTodayTimeRef.current + todaySessionElapsed;
        setQuantTodayTime(totalTodayElapsed);
        
        // Update vocab times
        const flashcardTime = parseInt(localStorage.getItem('gre_flashcard_time') || '0', 10);
        const synonymTime = parseInt(localStorage.getItem('gre_synonym_time') || '0', 10);
        const fastAttackTime = parseInt(localStorage.getItem('gre_fast_attack_time') || '0', 10);
        const hardTermsTime = parseInt(localStorage.getItem('gre_hard_terms_time') || '0', 10);
        setVocabTime(flashcardTime + synonymTime + fastAttackTime + hardTermsTime);
        
        const flashcardToday = getTodayTime('flashcard');
        const synonymToday = getTodayTime('synonym');
        const fastAttackToday = getTodayTime('fastattack');
        const hardTermsToday = getTodayTime('hardterms');
        setVocabTodayTime(flashcardToday + synonymToday + fastAttackToday + hardTermsToday);
        
        // Total time across all modes
        setTotalTime(getTotalTime());
        setTotalTodayTime(getTotalTodayTime());
        
        // Save to localStorage every 5 seconds
        if (sessionElapsed % 5 === 0 && sessionElapsed > 0) {
          try {
            localStorage.setItem('gre_quant_time', totalElapsed.toString());
            saveTodayTime('quant', totalTodayElapsed);
            // Update initial saved time to the new total, then reset session start
            initialSavedTimeRef.current = totalElapsed;
            initialTodayTimeRef.current = totalTodayElapsed;
            sessionStartTimeRef.current = Date.now();
            todaySessionStartRef.current = Date.now();
          } catch (e) {
            console.error('Error saving quant time to localStorage:', e);
          }
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [quantSessionActive]);

  // Problem timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (isTimerRunning && problemStartTimeRef.current) {
      interval = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - problemStartTimeRef.current!) / 1000);
        setProblemTime(elapsed);
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isTimerRunning]);

  const handleStartTimer = () => {
    addDebugLog('timer', 'Timer started', {
      currentProblem,
      timestamp: Date.now()
    });
    setQuantSessionActive(true);
    problemStartTimeRef.current = Date.now();
    setIsTimerRunning(true);
    setProblemTime(0);
    setShowFeedback(false);
  };

  const handleStopTimer = () => {
    const elapsed = problemStartTimeRef.current ? Math.floor((Date.now() - problemStartTimeRef.current) / 1000) : 0;
    addDebugLog('timer', 'Timer stopped', {
      currentProblem,
      elapsedSeconds: elapsed,
      timestamp: Date.now()
    });
    setIsTimerRunning(false);
    setShowFeedback(true);
  };

  const handlePass = () => {
    // Show feedback tracker (optional)
    setShowFeedbackTracker(true);
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Are you sure you want to clear all problem history? This cannot be undone.')) {
      return;
    }

    if (user?.id) {
      setIsClearingHistory(true);
      try {
        const [{ error: problemsError }, { error: errorsError }] = await Promise.all([
          dataService.quantProblems.deleteAllForUser(user.id),
          dataService.quantErrors.deleteAllForUser(user.id)
        ]);

        if (problemsError || errorsError) {
          addDebugLog('error', 'Failed to clear Supabase quant history', {
            problemsError,
            errorsError
          });
          alert('Failed to clear history from Supabase. Please try again.');
          return;
        }

        setProblems([]);
        setErrors([]);
        setCurrentProblem(1);
        localStorage.removeItem('gre_quant_problems');
        localStorage.removeItem('gre_quant_errors');
        addDebugLog('state', 'Cleared Supabase quant history', { userId: user.id });
      } catch (error: any) {
        addDebugLog('error', 'Unexpected error clearing Supabase history', {
          errorMessage: error?.message || String(error)
        });
        alert('Error clearing history. Please try again.');
      } finally {
        setIsClearingHistory(false);
      }
    } else {
      setProblems([]);
      setErrors([]);
      setCurrentProblem(1);
      localStorage.removeItem('gre_quant_problems');
      localStorage.removeItem('gre_quant_errors');
      addDebugLog('state', 'Cleared local quant history');
    }
  };

  const handleFeedbackComplete = async (feedbackData?: any) => {
    // After feedback is logged (or skipped), record the passed problem
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // Log feedback data if provided
    if (feedbackData) {
      addDebugLog('localStorage', `Received feedback data for pass record`, {
        errorType: feedbackData.errorType,
        errorMade: feedbackData.errorMade,
        difficulty: feedbackData.difficulty,
        hasNotes: !!feedbackData.notes,
        hasImage: !!feedbackData.image
      });
    }

    const imageForProblem = feedbackData?.image
      ? await uploadImageIfNeeded(feedbackData.image, 'feedback')
      : undefined;

    const newProblem: ProblemRecord = {
      id: Date.now().toString(),
      problemNumber: currentProblem,
      timeSpent: problemTime,
      passed: true,
      timestamp: Date.now(),
      date: dateStr,
      // Include feedback data if available (same structure as error records)
      ...(feedbackData && {
        errorType: feedbackData.errorType,
        errorMade: feedbackData.errorMade,
        difficulty: feedbackData.difficulty,
        notes: feedbackData.notes,
        image: imageForProblem || feedbackData.image
      })
    };

    let updatedProblems: ProblemRecord[] = user ? [...problems] : [];
    if (!user) {
      try {
        addDebugLog('localStorage', 'Starting to save passed problem', { newProblem });
        const savedProblems = localStorage.getItem('gre_quant_problems');
        if (savedProblems) {
          updatedProblems = JSON.parse(savedProblems);
          addDebugLog('localStorage', `Loaded ${updatedProblems.length} existing problems`);
        }
      } catch (e) {
        addDebugLog('error', `Error parsing problems from localStorage: ${e}`, e);
      }
    }

    const beforeFilter = updatedProblems.length;
    updatedProblems = updatedProblems.filter((p: ProblemRecord) => 
      !(p.problemNumber === currentProblem && p.passed === true)
    );
    if (!user) {
      addDebugLog('localStorage', `Filtered ${beforeFilter - updatedProblems.length} duplicate entries`);
    }

    updatedProblems.push(newProblem);
    setProblems(updatedProblems);

    if (user) {
      supabase.rpc('record_session', {
        p_user_id: user.id,
        p_xp_earned: 10,
      }).then(({ error }) => {
        if (error) console.error('[XP] quant record_session failed:', error.message);
      });
      addDebugLog('state', 'Saved passed problem (Supabase user)', {
        problemNumber: currentProblem,
        totalProblems: updatedProblems.length
      });
    } else {
      const jsonString = JSON.stringify(updatedProblems);
      addDebugLog('localStorage', `JSON string length: ${jsonString.length} characters`);
      try {
        localStorage.setItem('gre_quant_problems', jsonString);
        const verify = localStorage.getItem('gre_quant_problems');
        if (verify) {
          const verifyParsed = JSON.parse(verify);
          addDebugLog('localStorage', `Verified: ${verifyParsed.length} problems saved successfully`);
        } else {
          addDebugLog('error', 'ERROR: Save verification failed - localStorage is empty after save!');
        }
        addDebugLog('pass', `Saved passed problem #${currentProblem}`, newProblem);
      } catch (quotaError: any) {
        // Handle QuotaExceededError by cleaning up old entries
        if (quotaError.name === 'QuotaExceededError' || quotaError.message?.includes('quota')) {
          const storageSize = getLocalStorageSize();
          const jsonSize = new Blob([jsonString]).size;
          
          getStorageQuotaInfo().then(quotaInfo => {
            addDebugLog('error', 'QuotaExceededError detected for problems, cleaning up old entries', {
              currentProblemCount: problems.length,
              jsonStringLength: jsonString.length,
              jsonSizeBytes: jsonSize,
              jsonSizeMB: (jsonSize / 1024 / 1024).toFixed(2),
              localStorageTotalSize: storageSize.total,
              localStorageTotalSizeMB: (storageSize.total / 1024 / 1024).toFixed(2),
              localStorageBreakdown: storageSize.breakdown,
              storageQuotaInfo: quotaInfo,
              problemsWithImages: problems.filter((p: ProblemRecord) => p.image).length,
              problemsWithoutImages: problems.filter((p: ProblemRecord) => !p.image).length,
              errorName: quotaError.name,
              errorMessage: quotaError.message,
              timestamp: Date.now()
            });
          });
          
          addDebugLog('error', 'QuotaExceededError detected for problems, cleaning up old entries', {
            currentProblemCount: problems.length,
            jsonStringLength: jsonString.length,
            jsonSizeBytes: jsonSize,
            jsonSizeMB: (jsonSize / 1024 / 1024).toFixed(2)
          });
          
          // Remove images from old entries to save space (keep last 20 entries with images)
          let cleanedProblems = problems.map((p: ProblemRecord, index: number) => {
            // Keep images only for the last 20 entries, remove from older ones
            if (index < problems.length - 20 && p.image) {
              return { ...p, image: undefined };
            }
            return p;
          });
          
          // Try to save with images removed from old entries
          let finalProblems = cleanedProblems;
          let savedSuccessfully = false;
          let attempts = 0;
          
          // Try saving - only clean up if we actually hit quota error
          while (attempts < 10) {
            try {
              const testString = JSON.stringify(finalProblems);
              localStorage.setItem('gre_quant_problems', testString);
              // Success! Save worked
              setProblems([...finalProblems]);
              const removedCount = problems.length - finalProblems.length;
              if (removedCount > 0) {
                addDebugLog('localStorage', `Saved problems after cleanup: ${finalProblems.length} problems (removed ${removedCount} old entries)`);
                setToastMessage(`⚠️ Storage full. Removed ${removedCount} old entries to save space.`);
              } else {
                addDebugLog('localStorage', `Saved problems after removing images from old entries: ${finalProblems.length} problems`);
                setToastMessage('⚠️ Storage full. Images removed from old entries to save space.');
              }
              setShowToast(true);
              addDebugLog('pass', `Saved passed problem #${currentProblem} after cleanup`, newProblem);
              savedSuccessfully = true;
              return; // Exit early since we already showed a toast
            } catch (e: any) {
              // Still hitting quota - need more cleanup
              if (e.name === 'QuotaExceededError' || e.message?.includes('quota')) {
                attempts++;
                if (attempts <= 3) {
                  // Remove oldest 10 entries
                  finalProblems = cleanupOldEntries(finalProblems, Math.max(50, finalProblems.length - 10));
                } else if (attempts <= 6) {
                  // Remove oldest 20 entries
                  finalProblems = cleanupOldEntries(finalProblems, Math.max(30, finalProblems.length - 20));
                } else {
                  // Remove oldest 50 entries
                  finalProblems = cleanupOldEntries(finalProblems, Math.max(20, finalProblems.length - 50));
                }
              } else {
                // Different error - re-throw
                throw e;
              }
            }
          }
          
          // Last resort: save without images (only if we haven't saved successfully yet)
          if (!savedSuccessfully) {
            try {
              const noImageProblems = finalProblems.map((p: ProblemRecord) => ({ ...p, image: undefined }));
              const noImageString = JSON.stringify(noImageProblems);
              const noImageSize = new Blob([noImageString]).size;
              
              // Get detailed storage info before showing the message
              const storageSize = getLocalStorageSize();
              
              localStorage.setItem('gre_quant_problems', noImageString);
              setProblems([...noImageProblems]);
              setToastMessage('⚠️ Storage full. Images removed from all entries to save space.');
              setShowToast(true);
              
              // Comprehensive debug logging
              getStorageQuotaInfo().then(quotaInfo => {
                addDebugLog('storage_full', 'STORAGE FULL - Images removed from all entries', {
                  problemNumber: currentProblem,
                  action: 'pass_record_save',
                  attempts: attempts,
                  finalProblemsCount: noImageProblems.length,
                  noImageDataSize: noImageSize,
                  noImageDataSizeMB: (noImageSize / 1024 / 1024).toFixed(2),
                  localStorageTotalSize: storageSize.total,
                  localStorageTotalSizeMB: (storageSize.total / 1024 / 1024).toFixed(2),
                  localStorageBreakdown: storageSize.breakdown,
                  storageQuotaInfo: quotaInfo,
                  problemsWithImages: finalProblems.filter((p: ProblemRecord) => p.image).length,
                  problemsWithoutImages: finalProblems.filter((p: ProblemRecord) => !p.image).length,
                  timestamp: Date.now()
                });
              });
              
              addDebugLog('pass', `Saved passed problem #${currentProblem} after removing all images`, newProblem);
              return; // Exit early since we already showed a toast
            } catch (e) {
              // Even without images, still can't save - remove more entries
              const moreCleaned = cleanupOldEntries(finalProblems.map((p: ProblemRecord) => ({ ...p, image: undefined })), 20);
              localStorage.setItem('gre_quant_problems', JSON.stringify(moreCleaned));
              setProblems([...moreCleaned]);
              setToastMessage('⚠️ Storage full. Kept only the 20 most recent entries.');
              setShowToast(true);
              addDebugLog('pass', `Saved passed problem #${currentProblem} after aggressive cleanup`, newProblem);
              return; // Exit early since we already showed a toast
            }
          }
        } else {
          throw quotaError; // Re-throw if it's not a quota error
        }
      }
    }

    await syncProblemToSupabase(newProblem);

    // Show success toast (only if we didn't already show one)
    setToastMessage('✅ Feedback saved successfully!');
    setShowToast(true);
    
    // Move to next problem
    setCurrentProblem(prev => prev + 1);
    setProblemTime(0);
    setShowFeedback(false);
    setShowFeedbackTracker(false);
    
    // Scroll to top of the screen
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleFeedbackCancel = () => {
    // Skip feedback, just record the passed problem
    handleFeedbackComplete();
  };

  const handleFail = () => {
    addDebugLog('fail', `Problem #${currentProblem} marked as failed`, {
      currentProblem,
      problemTime,
      timestamp: Date.now()
    });
    // Show error form
    setShowErrorForm(true);
    addDebugLog('error_tracker', 'Showing error form', {
      problemNumber: currentProblem,
      timeSpent: problemTime
    });
  };

  const handleImageCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    addDebugLog('form', 'Image capture triggered', {
      hasFile: !!file,
      fileName: file?.name,
      fileSize: file?.size,
      fileType: file?.type
    });
    
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        addDebugLog('form', 'Image loaded successfully', {
          base64Length: base64String.length,
          originalSize: base64String.length
        });
        
        // Compress image before storing
        try {
          const compressedImage = await compressImage(base64String, 800, 0.7);
          addDebugLog('form', 'Image compressed', {
            originalSize: base64String.length,
            compressedSize: compressedImage.length,
            reduction: `${Math.round((1 - compressedImage.length / base64String.length) * 100)}%`
          });
          setImagePreview(compressedImage);
        } catch (error) {
          addDebugLog('error', 'Error compressing image, using original', {
            error,
            errorMessage: error instanceof Error ? error.message : String(error)
          });
          // Fallback to original if compression fails
          setImagePreview(base64String);
        }
      };
      reader.onerror = (error) => {
        addDebugLog('error', 'Error reading image file', {
          error,
          fileName: file.name,
          fileSize: file.size
        });
        alert('Error reading image file');
      };
      reader.readAsDataURL(file);
    } else {
      addDebugLog('error', 'No file selected for image capture');
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    const fileInput = document.getElementById('error-image-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleErrorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    addDebugLog('form', 'Error form submitted', {
      errorType,
      errorMade,
      difficulty,
      hasNotes: !!notes,
      hasImage: !!imagePreview,
      currentProblem,
      problemTime
    });
    
    if (!errorType || !errorMade || !difficulty) {
      addDebugLog('error', 'Form validation failed - missing required fields', {
        errorType,
        errorMade,
        difficulty
      });
      alert('Please select Error Type, Error Made, and Difficulty');
      return;
    }

    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const uploadedErrorImage = await uploadImageIfNeeded(imagePreview, 'error');

    const newError: QuantErrorRecord = {
      id: Date.now().toString(),
      errorType,
      errorMade,
      difficulty,
      notes: notes.trim(),
      timestamp: Date.now(),
      date: dateStr,
      image: uploadedErrorImage || imagePreview || undefined,
      problemNumber: currentProblem,
      timeSpent: problemTime,
      isFromProblemTracker: true
    };

    // Save error to localStorage
    try {
      addDebugLog('localStorage', 'Starting to save error', { newError });
      const savedErrors = localStorage.getItem('gre_quant_errors');
      let storedErrors = savedErrors ? JSON.parse(savedErrors) : [];
      addDebugLog('localStorage', `Found ${storedErrors.length} existing errors`);
      
      // Remove any auto-generated entry for this problem number
      const beforeFilter = storedErrors.length;
      storedErrors = storedErrors.filter((err: any) => 
        !(err.problemNumber === currentProblem && err.isFromProblemTracker && err.errorType === 'Not specified')
      );
      addDebugLog('localStorage', `Filtered ${beforeFilter - storedErrors.length} auto-generated entries`);
      
      storedErrors.push(newError);
      const jsonString = JSON.stringify(storedErrors);
      addDebugLog('localStorage', `JSON string length: ${jsonString.length} characters`);
      
      try {
        localStorage.setItem('gre_quant_errors', jsonString);
        
        // Update errors state immediately so UI reflects the change
        setErrors([...storedErrors]);
        addDebugLog('state', `Updated errors state with ${storedErrors.length} errors`);
        
        // Verify save
        const verify = localStorage.getItem('gre_quant_errors');
        if (verify) {
          const verifyParsed = JSON.parse(verify);
          addDebugLog('localStorage', `Verified: ${verifyParsed.length} errors saved successfully`);
        } else {
          addDebugLog('error', 'ERROR: Save verification failed - localStorage is empty after save!');
        }
        
        addDebugLog('error_complete', `Saved error for problem #${currentProblem}`, newError);
        
        // Close form immediately after successful save to prevent double submission
        setShowErrorForm(false);
      } catch (quotaError: any) {
        // Handle QuotaExceededError by cleaning up old entries
        if (quotaError.name === 'QuotaExceededError' || quotaError.message?.includes('quota')) {
          const storageSize = getLocalStorageSize();
          const jsonSize = new Blob([jsonString]).size;
          
          getStorageQuotaInfo().then(quotaInfo => {
            addDebugLog('error', 'QuotaExceededError detected, cleaning up old entries', {
              currentErrorCount: storedErrors.length,
              jsonStringLength: jsonString.length,
              jsonSizeBytes: jsonSize,
              jsonSizeMB: (jsonSize / 1024 / 1024).toFixed(2),
              localStorageTotalSize: storageSize.total,
              localStorageTotalSizeMB: (storageSize.total / 1024 / 1024).toFixed(2),
              localStorageBreakdown: storageSize.breakdown,
              storageQuotaInfo: quotaInfo,
              errorsWithImages: storedErrors.filter((e: any) => e.image).length,
              errorsWithoutImages: storedErrors.filter((e: any) => !e.image).length,
              errorName: quotaError.name,
              errorMessage: quotaError.message,
              timestamp: Date.now()
            });
          });
          
            addDebugLog('error', 'QuotaExceededError detected, cleaning up old entries', {
              currentErrorCount: storedErrors.length,
            jsonStringLength: jsonString.length,
            jsonSizeBytes: jsonSize,
            jsonSizeMB: (jsonSize / 1024 / 1024).toFixed(2)
          });
          
          // Remove images from old entries to save space (keep last 20 entries with images)
          // IMPORTANT: cleanedErrors already includes newError (it was pushed before the quota error)
          let cleanedErrors = storedErrors.map((err: any, index: number) => {
            // Keep images only for the last 20 entries, remove from older ones
            // Don't remove image from the new error (it's the last one)
            if (index < storedErrors.length - 20 && err.image) {
              return { ...err, image: undefined };
            }
            return err;
          });
          
          // Try to save with images removed from old entries
          let finalErrors = cleanedErrors;
          let savedSuccessfully = false;
          let attempts = 0;
          
          // Try saving - only clean up if we actually hit quota error
          while (attempts < 10) {
            try {
              const testString = JSON.stringify(finalErrors);
              localStorage.setItem('gre_quant_errors', testString);
              // Success! Save worked
              setErrors([...finalErrors]);
              const removedCount = storedErrors.length - finalErrors.length;
              if (removedCount > 0) {
                addDebugLog('localStorage', `Saved after cleanup: ${finalErrors.length} errors (removed ${removedCount} old entries)`);
                setToastMessage(`⚠️ Storage full. Removed ${removedCount} old entries to save space.`);
              } else {
                addDebugLog('localStorage', `Saved after removing images from old entries: ${finalErrors.length} errors`);
                setToastMessage('⚠️ Storage full. Images removed from old entries to save space.');
              }
              setShowToast(true);
              addDebugLog('error_complete', `Saved error for problem #${currentProblem} after cleanup`, newError);
              savedSuccessfully = true;
              // Close form immediately after successful save to prevent double submission
              setShowErrorForm(false);
              break; // Success - exit loop
            } catch (e: any) {
              // Still hitting quota - need more cleanup
              if (e.name === 'QuotaExceededError' || e.message?.includes('quota')) {
                attempts++;
                if (attempts <= 3) {
                  // Remove oldest 10 entries
                  finalErrors = cleanupOldEntries(finalErrors, Math.max(50, finalErrors.length - 10));
                } else if (attempts <= 6) {
                  // Remove oldest 20 entries
                  finalErrors = cleanupOldEntries(finalErrors, Math.max(30, finalErrors.length - 20));
                } else {
                  // Remove oldest 50 entries
                  finalErrors = cleanupOldEntries(finalErrors, Math.max(20, finalErrors.length - 50));
                }
              } else {
                // Different error - re-throw
                throw e;
              }
            }
          }
          
          // Last resort: save without images (only if we haven't saved successfully yet)
          if (!savedSuccessfully) {
            try {
              const noImageErrors = finalErrors.map((err: any) => ({ ...err, image: undefined }));
              const noImageString = JSON.stringify(noImageErrors);
              const noImageSize = new Blob([noImageString]).size;
              
              // Get detailed storage info before showing the message
              const storageSize = getLocalStorageSize();
              
              localStorage.setItem('gre_quant_errors', noImageString);
              setErrors([...noImageErrors]);
              setToastMessage('⚠️ Storage full. Images removed from all entries to save space.');
              setShowToast(true);
              
              // Comprehensive debug logging
              getStorageQuotaInfo().then(quotaInfo => {
                addDebugLog('storage_full', 'STORAGE FULL - Images removed from all entries', {
                  problemNumber: currentProblem,
                  action: 'error_record_save',
                  attempts: attempts,
                  finalErrorsCount: noImageErrors.length,
                  noImageDataSize: noImageSize,
                  noImageDataSizeMB: (noImageSize / 1024 / 1024).toFixed(2),
                  localStorageTotalSize: storageSize.total,
                  localStorageTotalSizeMB: (storageSize.total / 1024 / 1024).toFixed(2),
                  localStorageBreakdown: storageSize.breakdown,
                  storageQuotaInfo: quotaInfo,
                  errorsWithImages: finalErrors.filter((e: any) => e.image).length,
                  errorsWithoutImages: finalErrors.filter((e: any) => !e.image).length,
                  timestamp: Date.now()
                });
              });
              
              addDebugLog('error_complete', `Saved error for problem #${currentProblem} after removing all images`, newError);
              // Close form immediately after successful save to prevent double submission
              setShowErrorForm(false);
            } catch (e) {
              // Even without images, still can't save - remove more entries
              const moreCleaned = cleanupOldEntries(finalErrors.map((err: any) => ({ ...err, image: undefined })), 20);
              localStorage.setItem('gre_quant_errors', JSON.stringify(moreCleaned));
              setErrors([...moreCleaned]);
              setToastMessage('⚠️ Storage full. Kept only the 20 most recent entries.');
              setShowToast(true);
              addDebugLog('error_complete', `Saved error for problem #${currentProblem} after aggressive cleanup`, newError);
              setShowErrorForm(false);
            }
          }
          // Continue with rest of function - don't return
        } else {
          throw quotaError; // Re-throw if it's not a quota error
        }
      }
    } catch (e) {
      addDebugLog('error', `Error saving error: ${e}`, {
        error: e,
        errorMessage: e instanceof Error ? e.message : String(e),
        errorStack: e instanceof Error ? e.stack : undefined
      });
      console.error('Error saving error:', e);
      setToastMessage('❌ Error saving. Please try again.');
      setShowToast(true);
      return; // Return early only on actual errors, not quota cleanup
    }
    
    // Record the failed problem (following same pattern as error saving)
    const newProblem: ProblemRecord = {
      id: Date.now().toString(),
      problemNumber: currentProblem,
      timeSpent: problemTime,
      passed: false,
      timestamp: Date.now(),
      date: dateStr
    };

    // Save failed problem to localStorage
    try {
      addDebugLog('localStorage', 'Starting to save failed problem', { newProblem });
      const savedProblems = localStorage.getItem('gre_quant_problems');
      let problems = savedProblems ? JSON.parse(savedProblems) : [];
      addDebugLog('localStorage', `Found ${problems.length} existing problems`);
      
      // Remove any duplicate entry for this problem number (if exists)
      const beforeFilter = problems.length;
      problems = problems.filter((p: ProblemRecord) => 
        !(p.problemNumber === currentProblem && p.passed === false)
      );
      addDebugLog('localStorage', `Filtered ${beforeFilter - problems.length} duplicate entries`);
      
      problems.push(newProblem);
      const jsonString = JSON.stringify(problems);
      addDebugLog('localStorage', `JSON string length: ${jsonString.length} characters`);
      localStorage.setItem('gre_quant_problems', jsonString);
      
      // Update problems state immediately so UI reflects the change
      setProblems([...problems]);
      addDebugLog('state', `Updated problems state with ${problems.length} problems`);
      
      // Verify save
      const verify = localStorage.getItem('gre_quant_problems');
      if (verify) {
        const verifyParsed = JSON.parse(verify);
        addDebugLog('localStorage', `Verified: ${verifyParsed.length} problems saved successfully`);
      } else {
        addDebugLog('error', 'ERROR: Save verification failed - localStorage is empty after save!');
      }
      
      addDebugLog('fail_complete', `Saved failed problem #${currentProblem}`, newProblem);
    } catch (e) {
      addDebugLog('error', `Error saving failed problem: ${e}`, {
        error: e,
        errorMessage: e instanceof Error ? e.message : String(e),
        errorStack: e instanceof Error ? e.stack : undefined
      });
      console.error('Error saving failed problem:', e);
    }

    await syncErrorToSupabase(newError);
    syncProblemToSupabase(newProblem);

    // Reset form to prevent double submission
    setErrorType('');
    setErrorMade('');
    setDifficulty('');
    setNotes('');
    setImagePreview(null);
    const fileInput = document.getElementById('error-image-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    
    // Ensure form is closed (it should already be closed from earlier, but double-check)
    setShowErrorForm(false);
    setShowFeedback(false);
    
    // Show success toast (will be shown after any cleanup toast if applicable)
    setTimeout(() => {
      setToastMessage('✅ Error saved successfully!');
      setShowToast(true);
    }, 200);
    
    // Move to next problem
    const nextProblem = currentProblem + 1;
    addDebugLog('state', `Moving from problem #${currentProblem} to problem #${nextProblem}`);
    setCurrentProblem(nextProblem);
    setProblemTime(0);
    
    // Scroll to top of the screen
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleErrorCancel = () => {
    setShowErrorForm(false);
    setErrorType('');
    setErrorMade('');
    setDifficulty('');
    setNotes('');
    setImagePreview(null);
    const fileInput = document.getElementById('error-image-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Get error details for a problem
  const getErrorForProblem = (problemNumber: number) => {
    return errors.find((err: any) => err.problemNumber === problemNumber);
  };

  // Get feedback details for a problem
  const getFeedbackForProblem = (problemNumber: number) => {
    try {
      const savedFeedbacks = localStorage.getItem('gre_quant_feedback');
      if (savedFeedbacks) {
        const feedbacks = JSON.parse(savedFeedbacks);
        return feedbacks.find((fb: any) => fb.problemNumber === problemNumber);
      }
    } catch (e) {
      console.error('Error loading feedbacks:', e);
    }
    return null;
  };

  const startReview = () => {
    addDebugLog('review', 'Starting review mode', {
      totalProblems: problems.length,
      problems: problems.map(p => ({ id: p.id, problemNumber: p.problemNumber, passed: p.passed }))
    });
    setQuantSessionActive(true);
    setReviewMode(true);
    setReviewIndex(0);
  };

  const exitReview = () => {
    setReviewMode(false);
    setReviewIndex(0);
    setSelectedProblem(null);
  };

  const handleProblemClick = (problem: ProblemRecord) => {
    setSelectedProblem(problem);
  };

  const closeProblemDetails = () => {
    setSelectedProblem(null);
  };

  const handleSolveWithAI = async (problemId: string, image: string) => {
    setSolvingId(problemId);
    try {
      const { data, error } = await supabase.functions.invoke('solve-problem', {
        body: { image, problemId },
      });
      if (error?.message?.includes('daily_limit_reached') || data?.error === 'daily_limit_reached') {
        setShowAILimitBanner(true);
        setSolvingId(null);
        return;
      }
      if (error || !data?.solution) throw new Error(error?.message ?? 'No solution returned');

      // Update in-memory state
      setProblems(prev => prev.map(p =>
        p.id === problemId ? { ...p, aiSolution: data.solution } : p
      ));

      // Update localStorage cache
      try {
        const cached = localStorage.getItem('gre_quant_problems');
        if (cached) {
          const arr: ProblemRecord[] = JSON.parse(cached);
          localStorage.setItem('gre_quant_problems', JSON.stringify(
            arr.map(p => p.id === problemId ? { ...p, aiSolution: data.solution } : p)
          ));
        }
      } catch {}

    } catch (err: any) {
      setToastMessage('AI solver failed. Please try again.');
      setShowToast(true);
    } finally {
      setSolvingId(null);
    }
  };

  const handleLiveImageCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result as string;
      try {
        const compressed = await compressImage(base64, 800, 0.7);
        setLiveImage(compressed);
      } catch {
        setLiveImage(base64);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleLiveSolveAI = async () => {
    if (!liveImage) return;
    setLiveAISolving(true);
    setShowLiveAILimitBanner(false);
    try {
      const { data, error } = await supabase.functions.invoke('solve-problem', {
        body: { image: liveImage },
      });
      if (error?.message?.includes('daily_limit_reached') || data?.error === 'daily_limit_reached') {
        setShowLiveAILimitBanner(true);
        return;
      }
      if (error || !data?.solution) throw new Error(error?.message ?? 'No solution returned');
      setLiveAISolution(data.solution);
    } catch {
      setToastMessage('AI solver failed. Please try again.');
      setShowToast(true);
    } finally {
      setLiveAISolving(false);
    }
  };

  const passedProblems = problems.filter(p => p.passed).length;
  const failedProblems = problems.filter(p => !p.passed).length;
  const totalProblems = problems.length;
  const averageTime = totalProblems > 0
    ? Math.round(problems.reduce((sum, p) => sum + p.timeSpent, 0) / totalProblems)
    : 0;

  const timeBuckets = {
    fast: problems.filter(p => p.timeSpent <= 120).length,
    ok:   problems.filter(p => p.timeSpent > 120 && p.timeSpent <= 135).length,
    slow: problems.filter(p => p.timeSpent > 135 && p.timeSpent <= 150).length,
    over: problems.filter(p => p.timeSpent > 150).length,
  };
  const maxBucketCount = Math.max(...Object.values(timeBuckets), 1);
  const avgTimeColor = averageTime === 0
    ? TEXT_MUTED
    : averageTime <= 120
      ? 'var(--color-success)'
      : averageTime <= 135 ? '#F59E0B'
      : 'var(--color-danger)';

  // Calculate stats for problem types (only from failed problems) and mistake types
  const problemTypeStats: { [key: string]: number } = {};
  const bestPerformingProblemTypes: { [key: string]: number } = {};
  const mistakeTypeStats: { [key: string]: number } = {};

  // Count from problems - separate passed and failed
  problems.forEach(problem => {
    // Count problem types from failed problems only
    if (!problem.passed && problem.errorType && problem.errorType.trim() !== '' && problem.errorType !== 'Not specified') {
      problemTypeStats[problem.errorType] = (problemTypeStats[problem.errorType] || 0) + 1;
    }
    // Count problem types from passed problems (best performing)
    if (problem.passed && problem.errorType && problem.errorType.trim() !== '' && problem.errorType !== 'Not specified') {
      bestPerformingProblemTypes[problem.errorType] = (bestPerformingProblemTypes[problem.errorType] || 0) + 1;
    }
    // Count mistakes from failed problems only
    if (!problem.passed && problem.errorMade && problem.errorMade.trim() !== '' && problem.errorMade !== 'Not specified') {
      mistakeTypeStats[problem.errorMade] = (mistakeTypeStats[problem.errorMade] || 0) + 1;
    }
  });

  // Count from errors array (all errors are from failed problems)
  errors.forEach(error => {
    if (error.errorType && error.errorType.trim() !== '' && error.errorType !== 'Not specified') {
      problemTypeStats[error.errorType] = (problemTypeStats[error.errorType] || 0) + 1;
    }
    if (error.errorMade && error.errorMade.trim() !== '' && error.errorMade !== 'Not specified') {
      mistakeTypeStats[error.errorMade] = (mistakeTypeStats[error.errorMade] || 0) + 1;
    }
  });

  // Sort by count (descending) and get top items
  const topProblemTypes = Object.entries(problemTypeStats)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  
  const topBestPerformingTypes = Object.entries(bestPerformingProblemTypes)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  
  const topMistakeTypes = Object.entries(mistakeTypeStats)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const maxProblemTypeCount = topProblemTypes.length > 0 ? topProblemTypes[0][1] : 1;
  const maxBestPerformingCount = topBestPerformingTypes.length > 0 ? topBestPerformingTypes[0][1] : 1;
  const maxMistakeTypeCount = topMistakeTypes.length > 0 ? topMistakeTypes[0][1] : 1;

  // Sort problems by timestamp (most recent first) for review
  const sortedProblemsForReview = [...problems].sort((a, b) => b.timestamp - a.timestamp);

  if (showErrorForm) {
    return (
      <div style={{ padding: '10px', maxWidth: '800px', margin: '0 auto' }}>
        <Toast 
          message={toastMessage} 
          show={showToast} 
          onClose={() => setShowToast(false)} 
        />
        <div style={{
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: 'var(--color-surface-muted)',
          border: '1px solid var(--color-border-strong)',
          borderRadius: '16px'
        }}>
          <h2 style={{ margin: '0 0 10px 0', fontSize: '20px' }}>
            Problem #{currentProblem} - Log Your Error
          </h2>
          <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
            Time spent: {formatTime(problemTime)}
          </p>
        </div>
        
        <div style={{
          padding: '24px',
          backgroundColor: '#fff',
          border: '1px solid var(--color-danger)',
          borderRadius: '16px'
        }}>
          <form onSubmit={handleErrorSubmit}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px' }}>
                Error Type (Type of GRE Problem) *
              </label>
              <select
                value={errorType}
                onChange={(e) => setErrorType(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#fff'
                }}
              >
                <option value="">Select error type...</option>
                {errorTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px' }}>
                Error Made (Type of Mistake) *
              </label>
              <select
                value={errorMade}
                onChange={(e) => setErrorMade(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#fff'
                }}
              >
                <option value="">Select error made...</option>
                {errorMades.map(made => (
                  <option key={made} value={made}>{made}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px' }}>
                Problem Difficulty *
              </label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#fff'
                }}
              >
                <option value="">Select difficulty...</option>
                {difficulties.map(diff => (
                  <option key={diff} value={diff}>{diff}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px' }}>
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional notes about this error..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '14px' }}>
                Photo of Question (Optional)
              </label>
              <input
                id="error-image-input"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageCapture}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#fff'
                }}
              />
              {imagePreview && (
                <div style={{ marginTop: '10px', position: 'relative' }}>
                  <img
                    src={imagePreview}
                    alt="Question preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '300px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      display: 'block'
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    style={{
                      position: 'absolute',
                      top: '5px',
                      right: '5px',
                      padding: '5px 10px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--color-danger)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '999px',
                      fontWeight: 700
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleErrorCancel}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  backgroundColor: 'var(--color-text-muted)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '999px'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  backgroundColor: 'var(--color-success)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '999px',
                  fontWeight: 700
                }}
              >
                Save Error
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Review Mode — unified ProblemDetail
  if (reviewMode && problems.length > 0) {
    return (
      <ProblemDetail
        mode="review"
        problems={sortedProblemsForReview}
        initialIndex={reviewIndex}
        errors={errors}
        onBack={exitReview}
        getImageSrc={getImageSrc}
        onSolveWithAI={handleSolveWithAI}
        solvingId={solvingId}
        showAILimitBanner={showAILimitBanner}
        onDismissLimitBanner={() => setShowAILimitBanner(false)}
      />
    );
  }

  // Problem Details — unified ProblemDetail
  if (selectedProblem) {
    const historyIndex = sortedProblemsForReview.findIndex(p => p.id === selectedProblem.id);
    return (
      <ProblemDetail
        mode="history"
        problems={sortedProblemsForReview}
        initialIndex={historyIndex >= 0 ? historyIndex : 0}
        errors={errors}
        onBack={closeProblemDetails}
        getImageSrc={getImageSrc}
        onSolveWithAI={handleSolveWithAI}
        solvingId={solvingId}
        showAILimitBanner={showAILimitBanner}
        onDismissLimitBanner={() => setShowAILimitBanner(false)}
      />
    );
  }

  if (showFeedbackTracker) {
    return (
      <div style={{ padding: '10px', maxWidth: '800px', margin: '0 auto' }}>
        <Toast 
          message={toastMessage} 
          show={showToast} 
          onClose={() => setShowToast(false)} 
        />
        <div style={{
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: 'var(--color-success-light)',
          border: '1px solid var(--color-success)',
          borderRadius: '16px'
        }}>
          <h2 style={{ margin: '0 0 10px 0', fontSize: '20px' }}>
            Problem #{currentProblem} - Add Feedback (Optional)
          </h2>
          <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
            Time spent: {formatTime(problemTime)}
          </p>
        </div>
        <QuantFeedbackTracker
          defaultProblemNumber={currentProblem}
          onComplete={handleFeedbackComplete}
          onCancel={handleFeedbackCancel}
        />
      </div>
    );
  }

  // ── Stats for QuantTracker ────────────────────────────────────────────────
  const timedProblems = problems.filter(p => p.timeSpent > 0);
  const bestTimeSeconds  = timedProblems.length > 0 ? Math.min(...timedProblems.map(p => p.timeSpent)) : 0;
  const worstTimeSeconds = timedProblems.length > 0 ? Math.max(...timedProblems.map(p => p.timeSpent)) : 0;
  const passRate = totalProblems > 0 ? Math.round((passedProblems / totalProblems) * 100) : 0;

  const weeklyData = (() => {
    const weekMap = new Map<string, { passed: number; failed: number }>();
    problems.forEach(p => {
      const d = new Date(p.timestamp);
      const year = d.getFullYear();
      const jan1 = new Date(year, 0, 1);
      const weekNum = Math.ceil(((d.getTime() - jan1.getTime()) / 86_400_000 + jan1.getDay() + 1) / 7);
      const key = `${year}-${String(weekNum).padStart(2, '0')}`;
      const entry = weekMap.get(key) ?? { passed: 0, failed: 0 };
      if (p.passed) entry.passed++; else entry.failed++;
      weekMap.set(key, entry);
    });
    const sorted = Array.from(weekMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6);
    const maxTotal = Math.max(...sorted.map(([, v]) => v.passed + v.failed), 1);
    return sorted.map(([, v], i) => ({
      label: `W${i + 1}`,
      result: (v.passed >= v.failed ? 'pass' : 'fail') as 'pass' | 'fail',
      value: (v.passed + v.failed) / maxTotal,
    }));
  })();

  const handleLogResult = (result: 'pass' | 'fail', seconds: number) => {
    setProblemTime(seconds);
    if (result === 'pass') handlePass();
    else handleFail();
  };

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <Helmet>
        <title>GRE Quant Problem Tracker | Phrenos</title>
        <meta name="description" content="Log and review GRE quantitative problems. Track which problem types you miss, add notes, and monitor study time across sessions." />
        <meta property="og:title" content="GRE Quant Problem Tracker | Phrenos" />
        <meta property="og:description" content="Log and review GRE quantitative problems. Track which problem types you miss, add notes, and monitor study time across sessions." />
        <meta property="og:url" content="https://phrenos.app/quant" />
        <link rel="canonical" href="https://phrenos.app/quant" />
      </Helmet>
      <Toast
        message={toastMessage}
        show={showToast}
        onClose={() => setShowToast(false)}
      />

      {showAILimitBanner && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10000, backgroundColor: 'var(--color-surface-white)',
          border: '1.5px solid var(--color-primary)', borderRadius: 16,
          padding: '14px 18px', boxShadow: '0 8px 24px rgba(31,76,64,0.2)',
          maxWidth: 320, width: 'calc(100% - 32px)',
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-primary)', marginBottom: 4 }}>
            Trial limit reached
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 12, lineHeight: 1.4 }}>
            Trial members get 1 AI explanation per day. Upgrade to Pro for unlimited AI solves.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => { setShowAILimitBanner(false); navigate('/upgrade'); }}
              style={{
                flex: 1, padding: '9px 0', borderRadius: 10,
                backgroundColor: 'var(--color-primary)', color: '#fff',
                border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}
            >
              Upgrade to Pro
            </button>
            <button
              onClick={() => setShowAILimitBanner(false)}
              style={{
                padding: '9px 14px', borderRadius: 10,
                backgroundColor: 'transparent', color: 'var(--color-text-muted)',
                border: '1px solid var(--color-border)', fontSize: 13, cursor: 'pointer',
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* ── Main tracker UI ───────────────────────────────────────────────── */}
      <QuantTracker
        stats={{
          passed: passedProblems,
          failed: failedProblems,
          avgTime: averageTime > 0 ? formatTime(averageTime) : '0:00',
          bestTime: bestTimeSeconds > 0 ? formatTime(bestTimeSeconds) : '—',
          worstTime: worstTimeSeconds > 0 ? formatTime(worstTimeSeconds) : '—',
          passRate,
          totalProblems,
        }}
        weeklyData={weeklyData.length > 0 ? weeklyData : undefined}
        onLogResult={handleLogResult}
        onOpenHistory={startReview}
      />
    </div>
  );
};

export default QuantProblemTracker;

